import { TimeRange } from "@/stores";
import { Metadata, Numeric } from "@soft-db/d3-chart";

export type SerieSegment<T> = {
	start: Date;
	end: Date;
	data: SerieData<T>;
};

export type SerieData<T> = {
	value: T;
	metadata: Metadata;
};

export type SerieDataPoint<T> = {
	x: Numeric;
	y: T | null;
};

export type SearchFallback = "left" | "right";

function between(x: Numeric, x1: Numeric, x2: Numeric) {
	return x1 <= x && x <= x2;
}

export function segmentsToDataPoints<T>(segments: SerieSegment<T>[]): SerieDataPoint<T>[] {
	const points: SerieDataPoint<T>[] = [];

	for (const segment of segments) {
		const previousPoint = points.at(-1);
		if (previousPoint != null && previousPoint.x.valueOf() !== segment.start.valueOf()) {
			points.push({
				x: previousPoint.x,
				y: null,
			});
		}

		points.push(
			{
				x: segment.start,
				y: segment.data.value,
			},
			{
				x: segment.end,
				y: segment.data.value,
			}
		);
	}

	return points;
}

export class IntervalSerie<T, K> {
	id: string;
	query: K;
	segments: SerieSegment<T>[];

	constructor(id: string, query: K, segments: SerieSegment<T>[]) {
		this.id = id;
		this.query = query;
		this.segments = segments;
	}

	get range(): TimeRange<Date> | null {
		const firstSegment = this.segments.at(0);
		const lastSegment = this.segments.at(-1);

		if (firstSegment == null || lastSegment == null) {
			return null;
		} else {
			return {
				start: new Date(firstSegment.start),
				end: new Date(lastSegment.end),
			};
		}
	}

	merge(other: this): void {
		const currentRange = this.range;
		const otherRange = other.range;

		if (currentRange == null) {
			return;
		}

		if (otherRange == null) {
			return;
		}

		let segments: SerieSegment<T>[] | null = null;

		if (otherRange.end <= currentRange.start) {
			segments = [...other.segments, ...this.segments];
		} else if (otherRange.start >= currentRange.end) {
			segments = [...this.segments, ...other.segments];
		}

		// Fast path
		if (segments != null) {
			this.segments = segments;
			return;
		}

		let startMergeIdx: number | null = null;
		let endMergeIdx: number | null = null;

		if (between(otherRange.start, currentRange.start, currentRange.end)) {
			startMergeIdx = IntervalSerie.search(this.segments, otherRange.start);
		}

		if (between(otherRange.end, currentRange.start, currentRange.end)) {
			// Using `right` to force to always return something.
			// If the otherRange.end is ONLY the end of one segment, but never the start of another segment,
			// the search will return `null` without a fallback.
			// We do a `between` check first, so we need an index. Without the index, we may lose data.
			endMergeIdx = IntervalSerie.search(this.segments, otherRange.end, "right");
		}

		let left: SerieSegment<T>[] = [],
			middle: SerieSegment<T>[] = [],
			right: SerieSegment<T>[] = [];

		if (startMergeIdx != null && endMergeIdx != null) {
			//
			// The other serie is in the middle
			left = this.segments.slice(0, startMergeIdx);
			middle = other.segments;
			right = this.segments.slice(endMergeIdx);
		} else if (startMergeIdx != null) {
			//
			// The other serie overlap at the end
			left = this.segments.slice(0, startMergeIdx);
			right = other.segments;
		} else if (endMergeIdx != null) {
			//
			//The other serie overlap at the start
			left = other.segments;
			right = this.segments.slice(endMergeIdx);
		}

		segments = [...left, ...middle, ...right];
		if (segments.length === 0) {
			return;
		}

		this.segments = segments;
	}

	getInRange(start: Date, end: Date): SerieSegment<T>[] {
		if (this.range == null) {
			return [];
		}

		const startIdx = IntervalSerie.search(this.segments, start, "left")!;
		const endIdx = IntervalSerie.search(this.segments, end, "right")!;

		return this.segments.slice(startIdx, endIdx + 1);
	}

	getAt(time: Date, fallback?: SearchFallback | null): SerieSegment<T> | null {
		const idx = IntervalSerie.search(this.segments, time, fallback);
		if (idx == null) {
			return null;
		}

		return this.segments[idx];
	}

	dropOutOfRange(start: Date, end: Date): void {
		if (this.range == null) return;

		const startIdx = IntervalSerie.search(this.segments, start, "left")!;
		const endIdx = IntervalSerie.search(this.segments, end, "right")!;

		this.segments = this.segments.slice(startIdx, endIdx + 1);
	}

	static search(
		segments: SerieSegment<any>[],
		x: Numeric,
		fallback?: SearchFallback | null
	): number | null {
		const xVal = x.valueOf();

		let start = 0,
			end = segments.length - 1;

		while (start <= end) {
			const mid = Math.floor((start + end) / 2);

			const segment = segments[mid];

			const x1 = segment.start.valueOf();
			const x2 = segment.end.valueOf();

			if (xVal >= x1 && xVal < x2) {
				return mid;
			}

			if (x2 <= xVal) {
				start = mid + 1;
			} else {
				end = mid - 1;
			}
		}

		if (fallback == null) return null;

		switch (fallback) {
			case "left":
				return Math.max(0, end);
			case "right":
				return Math.min(start, segments.length - 1);
		}
	}
}
